import React, { useRef, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { getParameter } from "../util";

import SquareTilesRepeatableImg from "../assets/repeatable-backgrounds/square-tiles.jpg";

const Franchise = () => {
  const unmounted = useRef(false);
  const params = useParams();
  const [route] = useState(params.route);

  const [franchise, setFranchise] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [characterAnchor] = useState(getParameter("ch"));
  const [isLoading, setIsLoading] = useState(true);

  const getFranchiseData = useCallback(async () => {
    try {
      const franchiseDataResp = await axios.get(`/api/franchise/${route}`);
      if (unmounted.current) return;
      setFranchise(franchiseDataResp.data.franchise);
    } catch (error) {
      if (unmounted.current) return;
      console.error(error);
    }
  }, [route]);

  const getCharacterData = useCallback(async () => {
    try {
      setIsLoading(true);
      const characterDataResp = await axios.get(
        `/api/character?franchise_route=${route}`,
      );
      if (unmounted.current) return;
      const respCharacters = characterDataResp.data.characters;

      // if a character anchor is provided, sort that character to the top before pushing data to state
      // also store a product image as the character image on the character object for all characters
      if (characterAnchor) {
        let characterAnchorIndex = -1;
        for (let i = 0; i < respCharacters.length; i++) {
          if (
            respCharacters[i].products.length > 0 &&
            respCharacters[i].products[0].image_url
          ) {
            respCharacters[i].character_image_url =
              respCharacters[i].products[0].image_url;
          }
          if (respCharacters[i].character_name === characterAnchor) {
            characterAnchorIndex = i;
          }
        }
        if (characterAnchorIndex !== -1) {
          const anchorCharacterData = respCharacters[characterAnchorIndex];
          respCharacters.splice(characterAnchorIndex, 1);
          respCharacters.unshift(anchorCharacterData);
        }
      }

      setCharacters(respCharacters);
    } catch (error) {
      if (unmounted.current) return;
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [route, characterAnchor]);

  useEffect(() => {
    getFranchiseData();
  }, [getFranchiseData]);

  useEffect(() => {
    getCharacterData();
  }, [getCharacterData]);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  const loremIpsumText = `
    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
    eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
    enim ad minim veniam, quis nostrud exercitation ullamco laboris
    nisi ut aliquip ex ea commodo consequat.
  `;

  if (!franchise) return null;

  return (
    <Container>
      {!!franchise && (
        <TopContainer>
          <AvatarImg src={franchise.avatar_url} />
          <TopRightContainer>
            <Title>{franchise.name}</Title>
            <Description>
              {franchise.description ? franchise.description : loremIpsumText}
            </Description>
          </TopRightContainer>
        </TopContainer>
      )}
      {isLoading ? (
        <LoadingIndicator>
          <CircularProgress
            style={{ color: "inherit", width: "100%", height: "100%" }}
          />
        </LoadingIndicator>
      ) : (
        <React.Fragment>
          {/* <SubTitle>Characters</SubTitle> */}
          <CharacterNavTilesRow>
            <CharacterNavTilesBackgroundPattern />
            {characters.map((character) => (
              <CharacterNavTile
                key={`character_nav_tile_${character.influencer_id}`}
              >
                <CharacterNavTileImage
                  className="characterNavTileImage"
                  backgroundImg={character.character_image_url}
                />
                <CharacterNavTileText>
                  {character.character_name}
                </CharacterNavTileText>
                {!!character.influencer_name && (
                  <CharacterNavTileSignerContainer>
                    <SignedByText>Signed By</SignedByText>
                    <Signer>
                      <SignerAvatar src={character.influencer_avatar_url} />
                      <SignerName>{character.influencer_name}</SignerName>
                    </Signer>
                  </CharacterNavTileSignerContainer>
                )}
              </CharacterNavTile>
            ))}
          </CharacterNavTilesRow>
          {characters.map((character) => (
            <CharacterRow key={`character_${character.influencer_id}`}>
              <CharacterRowHeader>
                {/* <CharacterTitleImage backgroundImg={character.character_image_url} /> */}
                <CharacterRowTitle>
                  {character.character_name}
                </CharacterRowTitle>
                {!!character.influencer_name && (
                  <SignerContainer>
                    <SignedByText>Signed By</SignedByText>
                    <Signer>
                      <SignerAvatar src={character.influencer_avatar_url} />
                      <SignerName>{character.influencer_name}</SignerName>
                    </Signer>
                  </SignerContainer>
                )}
              </CharacterRowHeader>
              <ProductsContainer>
                {character.products.map((product) => {
                  return (
                    <ProductCard key={`product_${product.product_id}`}>
                      <ProductImage
                        className="productImage"
                        backgroundImg={product.image_url}
                      />
                      <ProductName>{product.name}</ProductName>
                    </ProductCard>
                  );
                })}
              </ProductsContainer>
            </CharacterRow>
          ))}
        </React.Fragment>
      )}
    </Container>
  );
};

export default Franchise;

const Container = styled.div``;

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 20px;
  background: linear-gradient(305deg, rgb(120, 120, 130), rgb(30, 30, 30));
  color: white;
  background: white;
  color: rgb(60, 60, 60);
`;

const AvatarImg = styled.img`
  height: 200px;
  width: auto;
  margin: 10px;
  border-radius: 3px;
  box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.2);
`;

const TopRightContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  margin: 20px;
  margin-top: 20px;
  font-size: 30px;
  font-weight: bold;
`;

const Description = styled.div`
  margin: 20px;
  margin-top: 0;
  max-width: 500px;
`;

// const SubTitle = styled.div`
//   margin: 20px;
//   margin-left: 40px;
//   font-size: 24px;
//   font-weight: bold;
// `;

const CharacterRow = styled.div`
  background-color: rgb(255, 255, 255);
  margin-top: 10px;
  box-shadow: -1px -1px 2px 1px rgba(0, 0, 0, 0.05);
`;

const CharacterRowHeader = styled.div`
  background-color: rgb(255, 255, 255);
  display: inline-block;
  padding: 10px;
  padding-right: 30px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid rgb(240, 240, 240);
`;

const CharacterRowTitle = styled.div`
  font-weight: bold;
  font-size: 20px;
  user-select: none;
  color: rgb(60, 60, 60);
  margin-left: 20px;
  margin-right: 10px;
  margin-top: 5px;
`;

const ProductsContainer = styled.div`
  margin: 10px;
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
`;

const ProductCard = styled.div`
  margin: 10px;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  color: inherit;
  &:hover {
    text-decoration: underline;
    > .productImage {
      box-shadow: 1px 1px 4px 2px rgba(0, 0, 0, 0.2);
      transform: scale(1.02);
    }
  }
`;

const ProductImage = styled.div`
  width: 180px;
  height: 250px;
  border-radius: 5px;
  ${(props) =>
    props.backgroundImg &&
    `
      background-image: url(${props.backgroundImg});
      background-position: center;
      background-size: cover;
    `}
`;

const ProductName = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin: 5px;
`;

const SignerContainer = styled.div`
  margin-left: 30px;
  cursor: pointer;
  user-select: none;
`;

const CharacterNavTileSignerContainer = styled.div`
  margin-top: 5px;
  cursor: pointer;
  user-select: none;
  text-align: center;
`;

const SignedByText = styled.div`
  font-size: 12px;
  color: rgb(150, 150, 150);
`;

const Signer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding-top: 2px;
  font-weight: bold;
  color: rgb(100, 100, 100);
  &:hover {
    text-decoration: underline;
  }
`;

const SignerName = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 110px;
`;

const SignerAvatar = styled.img`
  border-radius: 50%;
  width: 25px;
  height: 25px;
  object-fit: cover;
  margin-right: 5px;
  border: 1px solid rgb(125, 125, 125);
  box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.1);
`;

const LoadingIndicator = styled.div`
  margin: 20px;
  margin-left: 50px;
  width: 34px;
  height: 34px;
  color: rgb(60, 60, 60);
  margin-bottom: 80vh;
`;

const CharacterNavTilesRow = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  border-left: 1px solid rgb(240, 240, 240);
  border-right: 1px solid rgb(240, 240, 240);
  box-shadow: 0 1px 2px 1px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const CharacterNavTilesBackgroundPattern = styled.div`
  background-image: url(${SquareTilesRepeatableImg});
  background-size: 100px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.05;
  z-index: -1;
`;

const CharacterNavTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  user-select: none;
  background-color: rgb(255, 255, 255);
  padding: 10px;
  width: 150px;
  min-height: 160px;
  text-align: center;
  border-right: 1px solid rgb(240, 240, 240);
  border-bottom: 1px solid rgb(240, 240, 240);
  &:hover {
    background-color: rgb(240, 240, 240);
    > .characterNavTileImage {
      box-shadow: 1px 1px 2px 1px rgba(0, 0, 0, 0.1);
      transform: scale(1.01);
    }
  }
  &:active {
    background-color: rgb(230, 230, 230);
  }
`;

const CharacterNavTileImage = styled.div`
  width: 90px;
  height: 120px;
  border-radius: 5px;
  flex-shrink: 0;
  ${(props) =>
    props.backgroundImg &&
    `
      background-image: url(${props.backgroundImg});
      background-position: center;
      background-size: cover;
  `}
`;

const CharacterNavTileText = styled.div`
  margin-top: 5px;
  color: rgb(60, 60, 60);
  font-weight: bold;
`;

const CharacterTitleImage = styled.div`
  margin-left: 10px;
  width: 40px;
  height: 60px;
  border-radius: 5px;
  flex-shrink: 0;
  ${(props) =>
    props.backgroundImg &&
    `
      background-image: url(${props.backgroundImg});
      background-position: center;
      background-size: cover;
  `}
`;
